import { z } from "zod";
import { DatabaseSequenceSchema, DecimalStringSchema, OpaqueCursorSchema, PageLimitSchema, Rfc3339MillisSchema, Sha256HexSchema, UuidSchema, } from "./primitives.js";
export const UploadStateSchema = z.enum([
    "uploading",
    "quarantined",
    "scanning",
    "ready",
    "rejected_unsafe",
    "failed_transient",
]);
export const DocumentEditSessionStateSchema = z.enum([
    "opening",
    "active",
    "saving",
    "finalizing",
    "closed",
    "save_failed",
    "authorization_revoked",
]);
export const DocumentEditOutputStateSchema = z.enum([
    "quarantined",
    "scanning",
    "ready",
    "save_conflict",
    "revoked_session_recovery",
    "rejected_unsafe",
    "failed_transient",
    "promoted",
    "discarded",
]);
export const DocumentVersionCandidateSchema = z
    .object({
    candidateId: UuidSchema,
    documentId: UuidSchema,
    baseVersionId: UuidSchema,
    sha256: Sha256HexSchema,
    byteLength: DecimalStringSchema,
    mediaType: z.string().min(1).max(255),
    scanReceiptId: UuidSchema,
})
    .strict();
export const DocumentPatchOperationSchema = z
    .object({
    operation: z.enum([
        "replace_text",
        "insert_before",
        "insert_after",
        "delete_block",
        "set_literal",
        "clear",
        "set_style",
        "insert_row",
        "delete_row",
    ]),
    target: z.string().trim().min(1).max(512),
    value: z.union([z.string().max(1_048_576), z.number().finite(), z.boolean(), z.null()]).optional(),
})
    .strict();
const OfficeUserSchema = z
    .object({
    id: z.string().min(1).max(255),
    name: z.string().min(1).max(500).optional(),
})
    .strict();
const CallbackBaseShape = {
    key: z.string().min(1).max(512),
    users: z.array(z.string().min(1).max(255)).max(20).optional(),
    actions: z
        .array(z
        .object({
        type: z.number().int().min(0).max(3),
        userid: z.string().min(1).max(255),
    })
        .strict())
        .max(100)
        .optional(),
    lastsave: Rfc3339MillisSchema.optional(),
};
export const OnlyOfficeCallbackSchema = z.discriminatedUnion("status", [
    z.object({ ...CallbackBaseShape, status: z.literal(1) }).strict(),
    z.object({ ...CallbackBaseShape, status: z.literal(4) }).strict(),
    z
        .object({
        ...CallbackBaseShape,
        status: z.literal(2),
        url: z.string().url(),
        filetype: z.string().min(1).max(20).optional(),
        forcesavetype: z.never().optional(),
    })
        .strict(),
    z
        .object({
        ...CallbackBaseShape,
        status: z.literal(6),
        url: z.string().url(),
        filetype: z.string().min(1).max(20).optional(),
        forcesavetype: z.number().int().min(0).max(3),
    })
        .strict(),
    z
        .object({
        ...CallbackBaseShape,
        status: z.literal(3),
        error: z.number().int().optional(),
    })
        .strict(),
    z
        .object({
        ...CallbackBaseShape,
        status: z.literal(7),
        error: z.number().int().optional(),
        forcesavetype: z.number().int().min(0).max(3).optional(),
    })
        .strict(),
]);
export const OfficeEditorUserSchema = OfficeUserSchema;
export const DocumentSourceSchema = z.enum(["upload", "office", "markdown", "proposal", "git", "recovery"]);
export const DocumentCapabilitySchema = z.enum(["read_write", "read_only", "blocked"]);
export const DocumentSurfacePreviewSchema = z.enum(["markdown", "text", "table", "pdf", "office", "image", "download"]);
export const DocumentSurfaceEditorSchema = z.enum(["markdown", "office"]).nullable();
export const DocumentSurfaceCapabilitySchema = z
    .object({
    preview: DocumentSurfacePreviewSchema,
    editor: DocumentSurfaceEditorSchema,
    focusContext: z.boolean(),
    downloadOnly: z.boolean(),
})
    .strict();
export const DocumentProjectionSchema = z
    .object({
    id: UuidSchema,
    workspaceId: UuidSchema,
    ownerAccountId: UuidSchema,
    displayName: z.string().min(1).max(500),
    mediaType: z.string().min(1).max(255),
    ingestState: UploadStateSchema,
    currentVersionId: UuidSchema.nullable(),
    currentSha256: Sha256HexSchema.nullable(),
    currentByteLength: DecimalStringSchema.nullable(),
    revision: DatabaseSequenceSchema,
    aiCapability: DocumentCapabilitySchema,
    surfaceCapabilities: DocumentSurfaceCapabilitySchema,
    createdAt: Rfc3339MillisSchema,
    updatedAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentListSchema = z.object({ items: z.array(DocumentProjectionSchema).max(10_000) }).strict();
export const DocumentVersionProjectionSchema = z
    .object({
    id: UuidSchema,
    documentId: UuidSchema,
    sequence: DatabaseSequenceSchema,
    sha256: Sha256HexSchema,
    byteLength: DecimalStringSchema,
    mediaType: z.string().min(1).max(255),
    source: DocumentSourceSchema,
    actorAccountId: UuidSchema,
    createdAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentVersionListSchema = z.object({ items: z.array(DocumentVersionProjectionSchema).max(10_000) }).strict();
export const DocumentEditorGateSchema = z
    .object({
    document: DocumentProjectionSchema,
    editSessionId: UuidSchema,
    editorKey: z.string().regex(/^[A-Za-z0-9_-]{43,64}$/),
    keyGeneration: DecimalStringSchema,
    status: DocumentEditSessionStateSchema,
    mode: z.enum(["view", "edit"]),
    officeOrigin: z.string().url(),
    bootstrapToken: z.string().min(32).max(512).nullable(),
    expiresAt: Rfc3339MillisSchema.nullable(),
})
    .strict();
export const DocumentEditorFinalizeResultSchema = z
    .object({
    document: DocumentProjectionSchema,
    status: DocumentEditSessionStateSchema,
})
    .strict();
export const DocumentMarkdownDraftSchema = z
    .object({
    documentId: UuidSchema,
    baseVersionId: UuidSchema,
    savedStateVector: z.string().regex(/^[A-Za-z0-9_-]*$/).max(100_000),
    bytesBase64: z.string().max(33_554_432),
    revision: DatabaseSequenceSchema,
    updatedAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentSaveCommandSchema = z
    .object({
    commandId: UuidSchema,
    documentId: UuidSchema,
    baseVersionId: UuidSchema,
    expectedDocumentRevision: DatabaseSequenceSchema,
    savedStateVector: z.string().regex(/^[A-Za-z0-9_-]*$/).max(100_000),
    bytesBase64: z.string().max(33_554_432),
})
    .strict();
export const DocumentEditOutputSchema = z
    .object({
    id: UuidSchema,
    documentId: UuidSchema,
    state: DocumentEditOutputStateSchema,
    sha256: Sha256HexSchema.nullable(),
    byteLength: DecimalStringSchema.nullable(),
    baseVersionId: UuidSchema.nullable(),
    currentVersionId: UuidSchema.nullable(),
    createdAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentRedactionRegionSchema = z
    .object({
    page: z.number().int().min(1).max(500),
    x: z.number().finite().min(0).max(100_000),
    y: z.number().finite().min(0).max(100_000),
    width: z.number().finite().gt(0).max(100_000),
    height: z.number().finite().gt(0).max(100_000),
})
    .strict();
export const DocumentRedactionStageSchema = z
    .object({
    derivativeId: UuidSchema,
    documentId: UuidSchema,
    sourceVersionId: UuidSchema,
    sourceSha256: Sha256HexSchema,
    regions: z.array(DocumentRedactionRegionSchema).min(1).max(2_000),
    reason: z.string().trim().min(1).max(4_096),
})
    .strict();
export const DocumentRedactionDerivativeSchema = z
    .object({
    id: UuidSchema,
    documentId: UuidSchema,
    sourceVersionId: UuidSchema,
    sourceSha256: Sha256HexSchema,
    state: z.enum(["pending", "verifying", "verified", "rejected"]),
    sha256: Sha256HexSchema.nullable(),
    byteLength: DecimalStringSchema.nullable(),
    dpi: z.number().int().positive().nullable(),
    regions: z.array(DocumentRedactionRegionSchema).max(2_000),
    verifierVersion: z.string().max(200).nullable(),
    verifierResult: z.record(z.string(), z.unknown()),
    createdAt: Rfc3339MillisSchema,
    updatedAt: Rfc3339MillisSchema,
})
    .strict();
export const OfficeBootstrapBodySchema = z.object({ token: z.string().min(32).max(512) }).strict();
export const OfficeBootstrapResultSchema = z
    .object({
    redirectPath: z.string().startsWith("/"),
    sessionCookie: z.string().min(32).max(512).optional(),
    fileGrant: z.string().regex(/^[A-Za-z0-9_-]{32,512}$/),
    editorToken: z.string().min(100).max(10_000),
    expiresAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentOfficeFileParamsSchema = z.object({ grant: z.string().regex(/^[A-Za-z0-9_-]{32,512}$/) }).strict();
export const DocumentGitConflictStageCommandSchema = z
    .object({
    commandId: UuidSchema,
    relativePath: z.string().min(1).max(1000),
    manifestBaseVersionId: UuidSchema,
    expectedCurrentVersionId: UuidSchema,
    expectedSha256: Sha256HexSchema,
    bytesBase64: z.string().max(67_108_864),
})
    .strict();
export const DocumentGitConflictRestoreCommandSchema = z
    .object({
    commandId: UuidSchema,
    relativePath: z.string().min(1).max(1000),
    expectedCurrentVersionId: UuidSchema,
})
    .strict();
const MAX_DOCUMENT_UPLOAD_BYTES = 50 * 1024 * 1024;
export const DocumentUploadByteLengthSchema = DecimalStringSchema.refine((value) => BigInt(value) <= BigInt(MAX_DOCUMENT_UPLOAD_BYTES), "Expected a document upload byte length no greater than 50 MiB");
export const DocumentSearchCommandSchema = z
    .object({
    workspaceId: UuidSchema,
    query: z.string().trim().min(1).max(500),
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(100),
})
    .strict();
export const DocumentSearchResultSchema = z
    .object({
    items: z.array(DocumentProjectionSchema).max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const DocumentDownloadMetadataSchema = z
    .object({
    documentId: UuidSchema,
    versionId: UuidSchema,
    displayName: z.string().min(1).max(500),
    mediaType: z.string().min(1).max(255),
    byteLength: DecimalStringSchema,
    sha256: Sha256HexSchema,
})
    .strict();
export const DocumentEditOutputListQuerySchema = z
    .object({
    cursor: OpaqueCursorSchema.nullable().default(null),
    limit: PageLimitSchema.default(100),
})
    .strict();
export const DocumentEditOutputListSchema = z
    .object({
    items: z.array(DocumentEditOutputSchema).max(250),
    nextCursor: OpaqueCursorSchema.nullable(),
})
    .strict();
export const DocumentGitStatusSchema = z
    .object({
    documentId: UuidSchema,
    relativePath: z.string().min(1).max(1_000),
    state: z.enum(["untracked", "clean", "modified", "conflicted", "ahead", "behind", "diverged"]),
    documentVersionId: UuidSchema.nullable(),
    documentSha256: Sha256HexSchema.nullable(),
    repositorySha256: Sha256HexSchema.nullable(),
    observedAt: Rfc3339MillisSchema,
})
    .strict();
export const DocumentUploadMetadataSchema = z
    .object({
    commandId: UuidSchema,
    documentId: UuidSchema,
    workspaceId: UuidSchema,
    displayName: z.string().trim().min(1).max(500),
    mediaType: z.string().trim().min(1).max(255),
    byteLength: DocumentUploadByteLengthSchema,
    sha256: Sha256HexSchema,
})
    .strict();
export const DocumentUploadResultSchema = z
    .object({
    document: DocumentProjectionSchema,
    version: DocumentVersionProjectionSchema.nullable(),
})
    .strict();
export const DocumentWorkerStateEventSchema = z
    .object({
    eventId: UuidSchema,
    workspaceId: UuidSchema,
    documentId: UuidSchema,
    jobId: UuidSchema,
    state: z.enum(["queued", "dispatched", "running", "verified", "rejected", "failed"]),
    attempt: DatabaseSequenceSchema,
    code: z.string().min(1).max(200).nullable(),
    observedAt: Rfc3339MillisSchema,
})
    .strict();
//# sourceMappingURL=documents.js.map